export type AISystem = {
  id: string;
  name: string;
  // Role/actor code. Open string to support any regulation pack's roles
  // (EU: PROVIDER/DEPLOYER/…, LL144: EMPLOYER/EMPLOYMENT_AGENCY). The
  // `AISystemEntity` enum is retained as the EU-specific named constant set.
  entity: string;
  // The free-text provider entity name from the questionnaire — NOT the owning
  // tenant. `account` below is the tenant. Do not conflate the two.
  organizationId: string;
  organization: string | null;
  // The owning tenant. Populated for every role.
  account: { id: string; name: string; slug: string } | null;
  description: string | null;
  version: string;
  // Classification code. Open string to support any regulation pack's
  // classifications (EU: HIGH_RISK/…, LL144: IN_SCOPE/OUT_OF_SCOPE).
  riskClassification: string | null;
  complianceStatus: ComplianceStatus;
  assessmentType?: AssessmentType;
  complianceContact: string | null;
  accountId: string;
  ceMarkUnlocked: boolean;
  // The date the AEDT is (or was) first used, YYYY-MM-DD. LL144 only, and the one date in the
  // product that may legitimately be in the FUTURE — a planned first use is exactly the case where
  // the § 20-871(b)(2) notice window still matters. Null for a system that has not recorded one.
  aedtFirstUseDate?: string | null;
  createdAt: string;
  updatedAt: string;
  origin: AISystemOrigin;
  createdById: string; // ID of the user who created the system
  needs_delegation: boolean;
  owner: Owner;
  wizardCompleted: boolean;
  // Regulation-pack metadata (backend list rows already return these).
  regulationCode?: string;
  regulationName?: string;
  regulationPackVersionId?: string;
  roleLabel?: string;
  classificationLabel?: string;
  classificationSeverity?: number;
};

export type AISystemsResponse = {
  items?: AISystem[];
  total?: number;
};

export type BulkDeleteAISystemsRequest = {
  ids: string[];
};

export type BulkDeleteAISystemsResponse = {
  message: string;
  deletedCount: number;
  deletedIds: string[];
};

export enum RiskClassification {
  HIGH_RISK = 'HIGH_RISK',
  HIGH_RISK_EXCEPTION = 'HIGH_RISK_EXCEPTION',
  PROHIBITED = 'PROHIBITED',
  LIMITED_RISK = 'LIMITED_RISK',
  MINIMAL_RISK = 'MINIMAL_RISK',
  OUT_OF_SCOPE = 'OUT_OF_SCOPE',
  // LL144 packs classify coverage rather than a risk tier.
  IN_SCOPE = 'IN_SCOPE',
  EXCLUDED = 'EXCLUDED',
  OTHER = 'OTHER'
}

export type MonitoringInterval = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY';

export enum AISystemEntity {
  PROVIDER = 'PROVIDER',
  DEPLOYER = 'DEPLOYER',
  DISTRIBUTOR = 'DISTRIBUTOR',
  IMPORTER = 'IMPORTER'
}

export enum ComplianceStatus {
  INITIATED = 'INITIATED',
  CLASSIFYING = 'CLASSIFYING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLIANT = 'COMPLIANT',
  COMPLIANT_RISK_CLASSIFIED = 'COMPLIANT_RISK_CLASSIFIED',
  NON_COMPLIANT = 'NON_COMPLIANT',
  ATTENTION_REQUIRED = 'ATTENTION_REQUIRED'
}

export type AssessmentType =
  | 'QUESTIONNAIRE'
  | 'AI_POWERED'
  | 'REGULATION_ASSESSMENT';

export type AISystemOrigin = 'MANUAL' | 'BULK_IMPORT';

export type BulkImportTemplateFormat = 'csv' | 'xlsx';

export type Owner = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type ComplianceStatusCount = {
  status: string;
  count: number;
};

export type AISystemSummaryDto = {
  total: number;
  compliant: number;
  nonCompliant: number;
  byComplianceStatus: ComplianceStatusCount[];
  // Regulation-scoped scalar rollups from the summary endpoint.
  compliantRiskClassified: number;
  inProgress: number;
  initiated: number;
  classifying: number;
  attentionRequired: number;
};

// Frontend-facing filter option shape ({ value, label }) that FilterDropdown
// consumes. The backend's /ai-systems/filter-options returns `key`, which the
// service maps to `value`. A null dimension means the backend is not scoping it.
export type EntityFilterOption = {
  value: string;
  label: string;
};

export type RiskClassificationFilterOption = {
  value: string;
  label: string;
};

export type AiSystemFilterOptions = {
  entities: EntityFilterOption[] | null;
  riskClassifications: RiskClassificationFilterOption[] | null;
};

export type StartAiSystemRequest = {
  name: string;
  version: string;
  description: string;
  organization: string;
  complianceContact: string;
  assessmentType: AssessmentType | null;
  regulationCode?: string;
  aiSystemId?: string;
};

export type StartAiSystemResponse = {
  aiSystemId: string;
  assessmentType: AssessmentType;
  // Questionnaire-only fields. Absent for REGULATION_ASSESSMENT starts, where
  // the backend returns only { aiSystemId, assessmentType }.
  type?: string;
  questionnaireId?: string;
  question?: Question;
  step?: string;
  snapshot?: Snapshot;
  status?: string;
  questionnaireVersionId?: string;
};

export type QuestionAnswer = {
  questionId: string;
  answer: string[];
};

export type NextQuestion = {
  questionnaireId: string;
  type: string;
  question: Question;
  step: string;
  snapshot: Snapshot;
  status: string;
  questionnaireVersionId: string;
};

export type PreviousQuestion = {
  step: string;
  questions: Question[];
  snapshot: Snapshot;
};

export type Question = {
  id: string;
  step: string;
  section: string;
  title: string;
  type: string;
  source: string;
  options: Option[];
  hint?: string;
};

type Option = {
  value: string;
  label: string;
  description?: string;
  result?: string;
  selected?: string[];
  hint?: string;
  next?: string;
  labelHtml?: string;
  effects?: {
    type: string;
    value: string;
  }[];
};

type Snapshot = {
  answers: Record<string, string | string[]>;
  obligations: string[];
  statusChanges: string[];
  tags: string[];
};

export type AISystemOverviewResponse = {
  requirements: {
    currentStep: number;
    totalSteps: number;
    pendingSteps: number;
  };
  documentation: {
    completed: number;
    total: number;
  };
  complianceStatus: string;
  repositories: {
    count: number;
    hasMajor: boolean;
    monitoringInterval: MonitoringInterval;
    lastMonitoringCheckAt: string | null;
  };
  riskClassification: string | null;
  generalInfo: {
    entityType: string;
    version: string;
    organizationName: string;
    complianceContactPerson: string;
    description: string;
    createdAt: string;
  };
  users: {
    owner: UserInfo[];
    admin: UserInfo[];
    editor: UserInfo[];
    viewer: UserInfo[];
  };
  ceMark: CEMark | null;
  // Regulation-pack metadata + capability flags.
  regulationCode?: string;
  regulationName?: string;
  regulationPackVersionId?: string;
  roleLabel?: string;
  classificationLabel?: string;
  classificationSeverity?: number;
  requiresRoadmap?: boolean;
  // Reserved for future Documentation/CE-mark gating; not yet consumed on the FE.
  permitsConformityDocuments?: boolean;
};

export type AiSystemUserQuotaCheck = {
  canAdd: boolean;
  remaining: number;
  alreadyInAccount: boolean;
};

export type TransferOwnershipResponse = {
  message: string;
  aiSystemId: string;
  newOwnerId: string;
};

export type UserInfo = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
};

export type CEMark = {
  url: string;
  ceMarkId: Record<string, any>;
  euDeclarationOfConformityId: Record<string, any>;
  notifiedBodyNumber: string;
  issuedOn: string;
  certificateNumber: string;
};

export type ChangeAssessmentRequest = {
  regulationCode?: string;
  assessmentType: AssessmentType;
  contextTransfer?: boolean;
};

export type ChangeAssessmentToAIResponse = {
  aiSystemId: string;
  assessmentType: 'AI_POWERED';
  message: string;
  jobId: string;
  status: string;
};

type ChangeAssessmentToQuestionnaireResponse = StartAiSystemResponse & {
  assessmentType: 'QUESTIONNAIRE';
  message: string;
};

export type ChangeAssessmentResponse =
  | ChangeAssessmentToAIResponse
  | ChangeAssessmentToQuestionnaireResponse;

export type BulkImportSkippedRow = {
  rowNumber: number;
  name: string;
  version: string;
  reason: string;
  aiSystemId: string;
  complianceStatus: ComplianceStatus;
};

export type BulkImportError = {
  rowNumber: number;
  field: string;
  reason: string;
};

export type BulkImportFailureReport = {
  reportId: string;
  expiresAt: string;
  filename: string;
  contentType: string;
};

export type BulkImportResponse = {
  fileName: string;
  format: BulkImportTemplateFormat;
  totalDataRows: number;
  validRows: number;
  invalidRows: number;
  matchedRows: number;
  unmatchedRows: number;
  createdRows: number;
  updatedRows: number;
  successfulImports: number;
  skippedRows: number;
  skipped: BulkImportSkippedRow[];
  errors: BulkImportError[];
  failureReport: BulkImportFailureReport;
};
